import { UseFormReturn } from 'react-hook-form';

import {
  EntitlementSlugEnum,
  LockStatus,
  PermissionEnum,
} from '@electricjs/core_entity-client';
import { DEVICE_PRODUCT_TYPES } from '@/constants/devices';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Button,
  ColorsV2,
  Flex,
  getToken,
  IconInCircle,
  spaces,
  Text,
  useBreakpoint,
} from '@electricjs/arc';

import {
  AssetConditionOption,
  AssetStatusOption,
  ProductTypeOption,
  SYSTEM_MANAGED_ASSET_STATUSES,
} from '@/components/Assets/helpers/transformOptions';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import { useIsProdSupport } from '@/hooks/useIsProdSupport';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import { AssetDetailsContext } from '@/pages/Assets/AssetDetails';
import { AssetStatusChip } from '@/pages/Assets/AssetStatus';
import SiteModal from '@/pages/Assets/SiteModal';
import { Asset, AssetAssignmentEmployee } from '@/types/assets';

import getProductTypeIcon from '../helpers/getProductTypeIcon';
import getProductTypeLabel from '../helpers/getProductTypeLabel';
import AssetDetailsButtonMenu from './AssetDetailsButtonMenu/AssetDetailsButtonMenu';
import AssetFormField, { AssetFieldTypes } from './AssetFormField';
import AssetHeaderItem from './AssetHeaderItem';
import formatDate from '@/helpers/formatDate';
import { useGetSiteLocationsQuery } from '@/redux/slices/assetApiSlice';
import Map from './Map';
import useAssetAddress from './useAssetAddress';
import {
  useGetAssigneePackageId,
  useGetVisibleAssignee,
  useIsAssigneeHidden,
} from '@/hooks/useGetAssignee';
import { MDMStatusChip } from '@/pages/Hardware/DeviceManagement/DevicesTable/TableColumns/MDMStatusChip';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import MaybeLink from '@/components/Assets/AssetDetails/MaybeLink';
import { WarehouseReassignmentIcon } from '@/components/Assets/AssetDetails/WarehouseReassignmentIcon';
import { OrganizationPackageWithEntitlements } from '@/types/packages';
import EmployeeOffboardedWarningIcon from '@/components/People/EmployeeOffboardedWarningIcon';
import { UnlockDeviceButton } from '@/components/DeviceOverview/DeviceHealthMonitor/Actions/UnlockDeviceButton';
import { getFullName } from 'common/utils/getFullName';
import { RestrictedEmployee } from 'common/RestrictedEmployee';
import getDomainFromUrl from '@/components/Assets/helpers/getDomainFromUrl';
import { AssetFormData } from '@/pages/Assets/NewAssetForm';

export const StyledHeaderContainer = styled(Flex)`
  border-bottom: 1px solid ${getToken(ColorsV2.GRAY_LIGHT)};
  padding-bottom: 2.4rem;
`;

type AssetDetailsHeaderProps = {
  asset: Asset;
  formHandlers: UseFormReturn<AssetFormData>;
  productTypeOptions: ProductTypeOption[];
  employeeOptions: EmployeeOption[];
  siteOptions: { label: string; value: string }[];
  locationOptions: { label: string; value: string }[];
  assetStatusOptions: AssetStatusOption[];
  assetConditionOptions: AssetConditionOption[];
  isFetchingProductTypes: boolean;
  isFetchingEmployees: boolean;
  isFetchingSites: boolean;
  isUpdatingAsset: boolean;
  isFetchingLocations: boolean;
  isMultiPlanOrganization: boolean;
  organizationPackages: OrganizationPackageWithEntitlements[] | undefined;
};

function getAssetInformation(asset: Asset, assignee?: AssetAssignmentEmployee) {
  return {
    name: asset.name,
    productType: asset.productType,
    assignedTo: getFullName(assignee?.firstName, assignee?.lastName),
    assignedToEmployeeId: asset.assignedTo,
    purchaseDate: asset.purchaseDate,
    purchasePrice: asset.purchasePrice?.toString(),
    purchasePriceCurrencyCode: asset.purchasePriceCurrencyCode,
    purchasedFrom: asset.purchasedFromUrl,
    brand: asset.brand,
    model: asset.model,
    serialNumber: asset.serialNumber,
    warrantyExpirationDate: asset.warrantyExpirationDate,
    site: asset.site,
    locationId: asset.locationId,
  };
}

const AssetDetailsHeader = ({
  asset,
  formHandlers,
  productTypeOptions,
  employeeOptions,
  siteOptions,
  locationOptions,
  assetStatusOptions,
  assetConditionOptions,
  isFetchingProductTypes,
  isFetchingEmployees,
  isFetchingSites,
  isUpdatingAsset,
  isFetchingLocations,
  isMultiPlanOrganization,
  organizationPackages,
}: AssetDetailsHeaderProps) => {
  const { t } = useTranslation('assets');
  const { t: tCommon } = useTranslation('common');
  const getAssigneePackageId = useGetAssigneePackageId();
  const getAssignee = useGetVisibleAssignee();
  const isAssigneeHidden = useIsAssigneeHidden()(asset);
  const { md, lg, xl } = useBreakpoint();
  const navigate = useNavigate();
  const { isEditing, setIsEditing } = useContext(AssetDetailsContext);
  const { openModal, closeModal } = useGlobalUI();
  const [isMapError, setIsMapError] = useState(false);

  const canReadAssetLocation = useUserHasOrganizationPermission(
    PermissionEnum.Assetslocationread
  );

  const canUpdateAssetLocation = useUserHasOrganizationPermission(
    PermissionEnum.Assetslocationupdate
  );

  const canUpdateInternalNotes = useUserHasOrganizationPermission(
    PermissionEnum.Warehousingupdate
  );

  const isProductSupport = useIsProdSupport();

  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const { address, isEmployeeAddress } = useAssetAddress(asset);

  const {
    name: assetName,
    productType,
    assignedTo,
    assignedToEmployeeId,
    brand,
    model,
    serialNumber,
    warrantyExpirationDate,
    site,
    locationId,
    purchaseDate,
    purchasedFrom,
  } = getAssetInformation(asset, getAssignee(asset));
  const hasComeFromNavigation = history.state.idx > 0;

  const watched = formHandlers.watch();

  const isAssetAtGlobalSite = !!(asset.site && !asset.site?.organizationId);

  const canUpdateAssetAssigneeAndSite =
    !isAssetAtGlobalSite || canUpdateAssetLocation;

  const { data: siteLocations } = useGetSiteLocationsQuery(site?.id ?? '', {
    skip: !site?.id || !canReadAssetLocation,
  });

  const assetLocation = siteLocations?.find(
    location => location.id === locationId
  );

  const employeeAssociationExists = !!asset.assignedToEmployee;

  const isManagedExternally = !!asset.device?.mdmProviderId;

  const isDeviceLocked = asset.device?.lockStatus === LockStatus.Locked;

  const isDeviceType = DEVICE_PRODUCT_TYPES.includes(productType.name);

  return (
    <StyledHeaderContainer vertical>
      <Flex
        justifyContent={hasComeFromNavigation ? 'space-between' : 'flex-end'}
        width="100%"
        mb={spaces.space_xl}>
        {hasComeFromNavigation && (
          <Button
            id="button-asset-details-back"
            variant="outline"
            iconBefore="arrow-left"
            onClick={() => navigate(-1)}>
            {t('assetDetails.back')}
          </Button>
        )}

        {isEditing ? (
          <Flex columnGap={spaces.space_l}>
            <Button
              id="button-asset-details-cancel"
              variant="text"
              onClick={() => setIsEditing(false)}
              disabled={isUpdatingAsset}>
              {t('assetDetails.cancel')}
            </Button>
            <Button
              id="button-asset-details-save"
              type="submit"
              disabled={isUpdatingAsset}>
              {t('assetDetails.saveInformation')}
            </Button>
          </Flex>
        ) : (
          <Flex gap="2.4rem">
            {isDeviceLocked && (
              <UnlockDeviceButton
                assetId={asset.id}
                employeeName={assignedTo}
              />
            )}
            <AssetDetailsButtonMenu
              editProfile={() => setIsEditing(true)}
              assetId={asset.id}
            />
          </Flex>
        )}
      </Flex>
      <Flex
        width="100%"
        justifyContent="flex-start"
        columnGap="1.6rem"
        vertical={!xl}>
        <Flex columnGap="4.2rem">
          <Flex flexShrink={0}>
            <IconInCircle
              size={md ? '9.6rem' : '7rem'}
              icon={getProductTypeIcon(productType.name)}
              customIconSize={md ? '4.8rem' : '3.5rem'}
              background={ColorsV2.INFO_LIGHTEST}
              intent="neutral"
            />
          </Flex>
          <Flex vertical>
            <Flex vAlignContent="center" width="100%">
              <Text variant="heading-1" mr="3rem">
                {assetName}
              </Text>
            </Flex>
            <Flex gap="1rem">
              {!isEditing && (
                <Flex gap="8px">
                  <AssetStatusChip status={asset.status} />
                </Flex>
              )}
              {!isEditing &&
                (hasMdmEntitlement || isManagedExternally) &&
                employeeAssociationExists &&
                isDeviceType && (
                  <MDMStatusChip
                    device={asset}
                    isMultiPlanOrganization={isMultiPlanOrganization}
                    organizationPackages={organizationPackages}
                    assigneePackageId={getAssigneePackageId(asset)}
                  />
                )}
            </Flex>
            <Flex vertical={!xl} justifyContent="space-between">
              <Flex maxWidth="70rem" mt="1.8rem" flexWrap="wrap" gap="1.2rem">
                <AssetHeaderItem
                  label={t('newAssetForm.name')}
                  name="name"
                  value={assetName}
                />
                <AssetHeaderItem
                  label={t('newAssetForm.assetType')}
                  name="productType"
                  value={productType.id}
                  formattedValue={getProductTypeLabel(productType.name)}
                  fieldProps={{
                    fieldType: AssetFieldTypes.Select,
                    options: productTypeOptions,
                    isLoading: isFetchingProductTypes,
                    placeholder: t('assetDetails.selectAssetType'),
                  }}
                />
                <AssetHeaderItem
                  label={t('newAssetForm.assignedTo')}
                  name="assignedTo"
                  value={assignedTo}
                  fieldProps={{
                    fieldType: AssetFieldTypes.Select,
                    options: employeeOptions,
                    isLoading: isFetchingEmployees,
                    // A hidden assignee can't be shown or changed here; the form
                    // leaves them untouched on save.
                    placeholder: isAssigneeHidden
                      ? tCommon('restrictedEmployee.label')
                      : t('assetDetails.selectEmployee'),
                    isDisabled:
                      !canUpdateAssetAssigneeAndSite || isAssigneeHidden,
                  }}
                  editAddon={
                    !canUpdateAssetAssigneeAndSite && (
                      <WarehouseReassignmentIcon />
                    )
                  }>
                  {isAssigneeHidden ? (
                    <RestrictedEmployee
                      tooltip={tCommon('restrictedEmployee.assignedTooltip')}
                    />
                  ) : (
                    <Flex alignItems="center">
                      <EmployeeOffboardedWarningIcon
                        employeeId={assignedToEmployeeId}
                      />
                      <MaybeLink
                        url={`/employees/${assignedToEmployeeId}`}
                        value={assignedTo}
                        emptyValue={t('assetDetails.unassigned')}
                      />
                    </Flex>
                  )}
                </AssetHeaderItem>
                {isEditing && (
                  <AssetHeaderItem
                    label={t('newAssetForm.purchaseDate')}
                    name="purchaseDate"
                    value={purchaseDate}
                    formattedValue={formatDate(purchaseDate)}
                    fieldProps={{
                      fieldType: AssetFieldTypes.DatePicker,
                    }}
                  />
                )}
                {isEditing && (
                  <AssetHeaderItem
                    label={t('newAssetForm.purchasePrice')}
                    name="purchasePrice" // using the "purchasePrice" property to handle validation schema
                    fieldProps={{
                      fieldType: AssetFieldTypes.Price,
                      // manually overriding value/onChange, as the Asset form uses separated fields for price and currency
                      overrides: {
                        value: {
                          amount: watched.purchasePrice ?? '',
                          currencyCode: watched.purchasePriceCurrencyCode,
                        },
                        onChange: price => {
                          formHandlers.setValue(
                            'purchasePrice',
                            price?.amount,
                            {
                              shouldValidate: true,
                            }
                          );
                          formHandlers.setValue(
                            'purchasePriceCurrencyCode',
                            price.currencyCode,
                            {
                              shouldValidate: true,
                            }
                          );
                        },
                      },
                    }}
                  />
                )}
                {isEditing && (
                  <AssetHeaderItem
                    label={t('newAssetForm.purchasedFrom')}
                    name="purchasedFrom"
                    value={purchasedFrom}
                    url={purchasedFrom}
                    formattedValue={getDomainFromUrl(purchasedFrom)}
                    fieldProps={{
                      placeholder: t('newAssetForm.purchasedFromPlaceholder'),
                    }}
                  />
                )}
                <AssetHeaderItem
                  label={t('newAssetForm.brand')}
                  name="brand"
                  value={brand}
                />
                <AssetHeaderItem
                  label={t('newAssetForm.model')}
                  name="model"
                  value={model}
                />
                <AssetHeaderItem
                  label={t('newAssetForm.serialNumber')}
                  name="serialNumber"
                  value={serialNumber}
                />
                {/* Show the site and location items when the map is not displayed or it shows the employee address. */}
                {(isMapError || isEmployeeAddress) && site?.name && (
                  <>
                    <AssetHeaderItem
                      label={t('assetDetails.site')}
                      name="site"
                      value={site?.name}
                      readonly
                    />
                    {canReadAssetLocation && (
                      <AssetHeaderItem
                        label={t('assetDetails.location')}
                        name="location"
                        value={assetLocation?.name}
                        readonly
                      />
                    )}
                    {/* Empty box to fill the space so things align correctly */}
                    <Flex
                      width={md ? '18rem' : '20rem'}
                      grow={1}
                      columnGap="1.6rem"></Flex>
                  </>
                )}
                {isEditing && (
                  <Flex
                    grow={1}
                    columnGap="1.2rem"
                    alignItems={md ? 'center' : 'flex-start'}
                    width="100%"
                    vertical={!md}>
                    <Flex width={lg ? '66%' : '100%'}>
                      <AssetFormField
                        label={t('assetDetails.site')}
                        name="site"
                        fieldType={AssetFieldTypes.Select}
                        options={siteOptions}
                        isDisabled={!canUpdateAssetAssigneeAndSite}
                        labelAddon={
                          !canUpdateAssetAssigneeAndSite && (
                            <WarehouseReassignmentIcon />
                          )
                        }
                        isLoading={isFetchingSites}
                        placeholder={t('assetDetails.selectSite')}
                      />
                    </Flex>
                    <Flex grow={1} shrink={0}>
                      <Button
                        id="button-asset-details-add-site"
                        variant="outline"
                        iconBefore="add-circle"
                        onClick={() =>
                          openModal(
                            <SiteModal hideModal={() => closeModal()} />
                          )
                        }
                        mb="0.4rem">
                        {t('assetDetails.addSite')}
                      </Button>
                    </Flex>
                  </Flex>
                )}
                {isEditing && canUpdateAssetLocation && (
                  <Flex
                    grow={1}
                    columnGap="1.2rem"
                    alignItems={md ? 'center' : 'flex-start'}
                    width="100%"
                    vertical={!md}>
                    <Flex width={lg ? '66%' : '100%'}>
                      <AssetFormField
                        label={t('assetDetails.location')}
                        name="location"
                        fieldType={AssetFieldTypes.Select}
                        options={locationOptions}
                        isLoading={isFetchingLocations}
                        placeholder={t('assetDetails.selectLocation')}
                        isClearable={true}
                      />
                    </Flex>
                  </Flex>
                )}
                {isEditing && (
                  <>
                    <Flex
                      columnGap="1.6rem"
                      width={md ? '22rem' : '20rem'}
                      grow={1}>
                      <AssetFormField
                        name="customAssetId"
                        fieldType={AssetFieldTypes.TextInput}
                        label={t('assetDetails.assetId')}
                      />
                    </Flex>
                    <Flex
                      columnGap="1.6rem"
                      width={md ? '22rem' : '20rem'}
                      grow={1}>
                      <AssetFormField
                        name="status"
                        fieldType={AssetFieldTypes.Select}
                        label={t('newAssetForm.status')}
                        options={assetStatusOptions}
                        isDisabled={
                          SYSTEM_MANAGED_ASSET_STATUSES.has(asset.status) &&
                          !isProductSupport
                        }
                      />
                    </Flex>
                    <Flex
                      columnGap="1.6rem"
                      width={md ? '22rem' : '20rem'}
                      grow={1}>
                      <AssetFormField
                        name="condition"
                        fieldType={AssetFieldTypes.Select}
                        label={t('newAssetForm.condition')}
                        options={assetConditionOptions}
                      />
                    </Flex>
                    <Flex columnGap="1.6rem" width={md ? '44rem' : '40rem'}>
                      <AssetHeaderItem
                        label={t('newAssetForm.warrantyExpiration')}
                        name="warrantyExpirationDate"
                        value={warrantyExpirationDate}
                        formattedValue={formatDate(warrantyExpirationDate)}
                        fieldProps={{
                          fieldType: AssetFieldTypes.DatePicker,
                          isClearable: true,
                        }}
                      />
                    </Flex>
                    <Flex columnGap="1.6rem" width={md ? '44rem' : '40rem'}>
                      <AssetFormField
                        label={t('newAssetForm.notes')}
                        name="notes"
                        fieldType={AssetFieldTypes.TextArea}
                        placeholder={t('assetDetails.additionalInformation')}
                      />
                    </Flex>
                    {canUpdateInternalNotes && (
                      <Flex columnGap="1.6rem" width={md ? '44rem' : '40rem'}>
                        <AssetFormField
                          label={t('newAssetForm.internalNotes')}
                          name="internalNotes"
                          fieldType={AssetFieldTypes.TextArea}
                          placeholder={t('assetDetails.internalInformation')}
                        />
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Flex vertical ml={xl ? 0 : 0} mt={xl ? 0 : '2.4rem'} gap="0.4rem">
          {/* Show the map only when the asset has an address (either from the employee it is assigned to or the site where it is located) and display the items below accordingly. */}
          {address && !isEditing && (
            <Map address={address} width="30rem" handleMapError={setIsMapError}>
              {!isEmployeeAddress && (
                <Flex flexWrap="wrap" width="100%" gap="1.2rem">
                  <AssetHeaderItem
                    containerProps={{ width: 'auto', flexGrow: 0 }}
                    label={t('assetDetails.site')}
                    name="site"
                    value={site?.name}
                    readonly
                  />
                  {canReadAssetLocation && (
                    <AssetHeaderItem
                      containerProps={{ width: 'auto', flexGrow: 0 }}
                      label={t('assetDetails.location')}
                      name="location"
                      value={assetLocation?.name}
                      readonly
                    />
                  )}
                </Flex>
              )}
            </Map>
          )}
        </Flex>
      </Flex>
    </StyledHeaderContainer>
  );
};

export default AssetDetailsHeader;
